import * as React from 'react'
import { Row, Col, Container } from 'components/Layout'
import Page from 'components/Page'
import PageHeader from 'components/Page/PageHeader'
import { Crumbs } from 'components/BreadCrumbs'
import cn from 'classnames'
import * as sLay from 'styles/modules/layout.scss'
import * as sText from 'components/Text/Text.scss'
import * as sMarg from 'styles/modules/margins.scss'
import * as sAnim from 'components/Animation/Animation.scss'
import { CallsByOriginPie } from '../../features/CallAnalytics/Cards/CallsByOriginPie'
import { CallsByTimeOfDayHeatmap } from '../../features/CallAnalytics/Cards/CallsByTimeOfDayHeatmap'
import { CallsByState } from '../../features/CallAnalytics/Cards/CallsByState'
import { CallsTrendsVolume } from '../../features/CallAnalytics/Cards/CallsTrendsVolume'
import { CallsTrendsDuration } from '../../features/CallAnalytics/Cards/CallsTrendsDuration'
import { CallsByStatus } from '../../features/CallAnalytics/Cards/CallsByStatus'
import { CallsByLocationTable } from '../../features/CallAnalytics/Cards/CallsByLocationTable'
import { KeyMetrics } from '../../features/CallAnalytics/Cards/KeyMetrics'
import { CallsRepeatCallers } from '../../features/CallAnalytics/Cards/CallsRepeatCallers'
import EmptyMessageCard from 'components/Cards/EmptyMessageCard'
import { Field } from 'redux-form'
import SelectPretty from 'components/Input/SelectPretty'
import Button from 'components/Button'
import * as s from './CallAnalytics.scss'
import { compact } from 'lodash'
import { getDateInterval } from '../../features/CallAnalytics/utils/getDateInterval'
import { getCallsForPeriod } from '../../features/CallAnalytics/utils/getCallsForPeriod'
import { dispatchToProps, ICallAnalyticsContainerProps } from './CallAnalytics.container'
import { IPeriodUnit } from 'features/CallAnalytics/types'

const period = [
  {
    name: 'Last 24h',
    period: '1-day',
    sumPeriod: 'hour',
    dateFormat: 'ha', // 2pm
  },
  {
    name: 'Last week',
    period: '1-week',
    sumPeriod: 'day',
    dateFormat: 'D MMM', // 10 Aug
  },
  {
    name: 'Last month',
    period: '28-day',
    sumPeriod: 'day',
    dateFormat: 'D MMM', // 10 Aug
  },
  {
    name: 'Last 3 months',
    period: '3-month',
    sumPeriod: 'week',
    dateFormat: 'D MMM', // 10 Aug
  },
  {
    name: 'Last Year',
    period: '12-month',
    sumPeriod: 'month',
    dateFormat: 'DD/MM/YY', // 10 Aug
  },
]

const periodOptions = period.map(({ name, period: value }) => ({ name, value }))

const getPeriodValueUnit = (periodString = '1-week') => {
  const periodStringSplit = periodString.split('-')
  return {
    periodValue: parseInt(periodStringSplit[0], 10),
    periodUnit: periodStringSplit[1],
  }
}

const getPeriod = (
  periodString = '1-week'
): { period: string; dateFormat: string; sumPeriod: IPeriodUnit } =>
  period.find(item => item.period === periodString) as any

export interface ICallAnalyticsProps extends ICallAnalyticsContainerProps {
  showAnalyticsShareModal: typeof dispatchToProps['showAnalyticsShareModal']
  getCallUsage: typeof dispatchToProps['getCallUsage']
  getNumbersAnalytics: typeof dispatchToProps['getNumbersAnalytics']
  replace: typeof dispatchToProps['replace']
  limit: number
  services: any
  servicesLoading: boolean
  serviceIds: string[]
  callUsage: any
  page: number
  formData: any
  cacheKey: string
  isDashboardPage: boolean
}

export class CallAnalytics extends React.Component<ICallAnalyticsProps> {
  isComponentMounted = false
  componentDidMount() {
    this.isComponentMounted = true
    const {
      getNumbersAnalytics,
      getCallUsage,
      serviceIds,
      page,
      formData: { dateFilter },
      limit,
      cacheKey,
      isDashboardPage,
      location,
    } = this.props
    if (serviceIds && serviceIds.length > 0) {
      getCallUsage({
        numberIds: serviceIds,
        page,
        limit,
        dateFilter,
        cacheKey,
        token: location.query.token,
      })
    }
    if (isDashboardPage) {
      getNumbersAnalytics().then(this.goToDefault)
    }
  }
  componentWillUnmount() {
    this.isComponentMounted = false
  }
  componentWillReceiveProps(nextProps) {
    const prevProps = this.props
    if (prevProps.cacheKey !== nextProps.cacheKey) {
      const {
        getCallUsage,
        serviceIds,
        page,
        formData: { dateFilter },
        limit,
        cacheKey,
        location,
      } = nextProps
      if (serviceIds && serviceIds.length > 0) {
        getCallUsage({
          numberIds: serviceIds,
          page,
          limit,
          dateFilter,
          cacheKey,
          token: location.query.token,
        })
      }
    }
  }
  goToDefault = ({ value: { data } }) => {
    const { serviceIds, replace } = this.props
    if (serviceIds.length === 0 && this.isComponentMounted && data[0]) {
      replace({
        pathname: '/dashboard/analytics',
        query: { serviceIds: data[0].id },
      })
    }
  }
  selectService = serviceIds => {
    const { replace } = this.props
    replace({
      pathname: '/dashboard/analytics',
      query: { serviceIds: serviceIds.join('-') },
    })
  }
  exportCsv = calls => {
    const keys = Object.keys(calls[0] || {})
    const data = [keys.join(','), ...calls.map(call => keys.map(key => call[key]).join(','))].join(
      '\r\n'
    )
    return `data:application/csv;charset=utf-8,${encodeURIComponent(data)}`
  }
  render() {
    const {
      callUsage,
      formData,
      services,
      serviceIds,
      servicesLoading,
      showAnalyticsShareModal,
      isDashboardPage,
    } = this.props

    const serviceOptions = services.map(service => ({
      name: compact([service.number, service.customerReference]).join(' - '),
      value: service.id,
    }))

    const { periodValue, periodUnit } = getPeriodValueUnit(formData.period)
    const { sumPeriod, dateFormat } = getPeriod(formData.period)
    const dateInterval = getDateInterval({ periodValue, periodUnit, sumPeriod })
    const { calls, callsPrevious } = getCallsForPeriod({
      callsRaw: callUsage.data,
      periodValue,
      periodUnit,
    })

    const inner = (
      <Container paddingTop paddingBottom className={cn('lg', sAnim.comeUp)}>
        <Row className={cn(sLay['layout-gt-xs-row'], sLay['layout-xs-column'], sMarg.mb30)}>
          <Col className={cn(s.col1, sLay['layout-row'])}>
            {isDashboardPage && (
              <div className={sLay.flex} style={{ marginRight: '10px' }}>
                <Field
                  label="Number"
                  component={SelectPretty}
                  multiselect
                  name="serviceIds"
                  options={serviceOptions}
                  display="box"
                  onChangeAlt={this.selectService}
                />
              </div>
            )}
            <div className={sLay.flex} style={isDashboardPage ? { marginLeft: '10px' } : {}}>
              <Field
                label="Period"
                component={SelectPretty}
                name="period"
                options={periodOptions}
                display="box"
              />
            </div>
          </Col>
          <Col className={cn(s.col2, { [s.hideCol]: serviceIds.length === 0 })}>
            <Row className={cn(sLay['layout-row'], 'sm')}>
              <Col className={cn('sm', sLay['flex'])}>
                <Button
                  className={s.downloadCsv}
                  color="tertiary"
                  rect
                  style={{ width: '100%', height: '100%', lineHeight: '55px' }}
                  download={`calls-${new Date()}-${formData.period}.csv`}
                  href={this.exportCsv(calls)}
                  target="_blank"
                >
                  Export CSV
                </Button>
              </Col>
              {isDashboardPage && serviceIds && serviceIds[0] && (
                <Col className={cn('sm', sLay['flex'])}>
                  <Button
                    className={s.downloadCsv}
                    style={{ width: '100%', height: '100%', lineHeight: '55px' }}
                    color="primary"
                    rect
                    onClick={() => showAnalyticsShareModal({ serviceId: serviceIds[0] })}
                  >
                    Public Link
                  </Button>
                </Col>
              )}
            </Row>
          </Col>
        </Row>
        {serviceIds.length > 0 ? (
          <div>
            <div className={cn(sText.descrip2, sMarg.mb30)}>How has Call Volume changed?</div>
            <Row className={cn(sLay['layout-gt-xs-row'], sLay['layout-xs-column'])}>
              <Col className={cn(sMarg.mb30, s.col1)}>
                <CallsTrendsVolume
                  calls={calls}
                  callsPrevious={callsPrevious}
                  dateInterval={dateInterval}
                  sumPeriod={sumPeriod}
                  periodValue={periodValue}
                  periodUnit={periodUnit}
                  dateFormat={dateFormat}
                  loading={callUsage.loading}
                />
              </Col>
              <Col className={cn(sMarg.mb30, s.col2)}>
                <KeyMetrics
                  calls={calls}
                  callsPrevious={callsPrevious}
                  loading={callUsage.loading}
                  periodValue={periodValue}
                  periodUnit={periodUnit}
                />
              </Col>
            </Row>
            <Row className={cn(sLay['layout-gt-xs-row'], sLay['layout-xs-column'])}>
              <Col className={cn(sMarg.mb30, s.col1)}>
                <CallsTrendsDuration
                  calls={calls}
                  callsPrevious={callsPrevious}
                  dateInterval={dateInterval}
                  sumPeriod={sumPeriod}
                  periodValue={periodValue}
                  periodUnit={periodUnit}
                  dateFormat={dateFormat}
                  loading={callUsage.loading}
                />
              </Col>
              <Col className={cn(sMarg.mb30, s.col2)}>
                <CallsByTimeOfDayHeatmap calls={calls} loading={callUsage.loading} />
              </Col>
            </Row>
            <div className={cn(sText.descrip2, sMarg.mb30)}>Where do your calls originate?</div>
            <Row className={cn(sLay['layout-gt-xs-row'], sLay['layout-xs-column'])}>
              <Col className={cn(sMarg.mb30, s.col2)}>
                <CallsByLocationTable calls={calls} loading={callUsage.loading} />
              </Col>
              <Col className={cn(sMarg.mb30, s.col2)}>
                <CallsByOriginPie calls={calls} loading={callUsage.loading} />
              </Col>
              <Col className={cn(sMarg.mb30, s.col2)}>
                <CallsByState calls={calls} loading={callUsage.loading} />
              </Col>
            </Row>
            <div className={cn(sText.descrip2, sMarg.mb30)}>How often do people call back?</div>
            <Row className={cn(sLay['layout-gt-xs-row'], sLay['layout-xs-column'])}>
              <Col className={cn(sLay.flex, sMarg.mb30)}>
                <CallsRepeatCallers
                  calls={calls}
                  height={400}
                  dateInterval={dateInterval}
                  sumPeriod={sumPeriod}
                  dateFormat={dateFormat}
                  loading={callUsage.loading}
                />
              </Col>
            </Row>
            <div className={cn(sText.descrip2, sMarg.mb30)}>How are calls handled?</div>
            <Row className={cn(sLay['layout-gt-xs-row'], sLay['layout-xs-column'])}>
              <Col className={cn(sMarg.mb30, sLay.flex)}>
                <CallsByStatus
                  calls={calls}
                  dateInterval={dateInterval}
                  sumPeriod={sumPeriod}
                  dateFormat={dateFormat}
                  loading={callUsage.loading}
                />
              </Col>
            </Row>
          </div>
        ) : (
          <EmptyMessageCard
            loading={servicesLoading}
            title="No number selected"
            subtitle="Use the 'Number' and 'Period' selectors above to load call analytics."
          />
        )}
      </Container>
    )

    if (isDashboardPage) {
      return (
        <Page layout={{ header: 'app' }} title="Call Analytics">
          <PageHeader>
            <Crumbs title="Call Analytics" />
          </PageHeader>
          {inner}
        </Page>
      )
    } else {
      return <div>{inner}</div>
    }
  }
}



// WEBPACK FOOTER //
// src/pages/CallAnalytics/CallAnalytics.tsx